import { createSelector } from 'reselect'

import { isAvailableFeatureSelector } from 'features/billing'

import { GROUP_MAPPING_DEFAULT_ROWS_SIZE, GROUP_MAPPING_STATES, SCIM_STATES } from './constants'
import { generateEmptyMappings } from './helpers'

const sliceSelector = (state) => state.organizationSettings.userProvisioning

const scimSettingsSelector = createSelector(
  sliceSelector,
  ({ scimSettings }) => scimSettings
)

const scimStateSelector = createSelector(
  scimSettingsSelector,
  (settings) => settings && settings.state
)

export const attributeValuesSelector = createSelector(
  sliceSelector,
  ({ attributeValues }) => attributeValues
)

export const baseUrlSelector = createSelector(
  scimSettingsSelector,
  (settings) => (settings && settings.base_url) || ''
)

export const bearerTokensSelector = createSelector(
  scimSettingsSelector,
  (settings) => (settings && settings.bearer_tokens) || []
)

export const currentStepSelector = createSelector(
  sliceSelector,
  ({ currentStep }) => currentStep
)

export const defaultUserGroupSelector = createSelector(
  scimSettingsSelector,
  (settings) => settings?.default_user_group
)
export const groupMappingsSelector = createSelector(
  sliceSelector,
  ({ groupMappings }) => {
    const mappings = groupMappings.map((mapping) => (
      {
        id: mapping.id,
        groupId: mapping.user_group.id,
        attrValue: mapping.attribute_value
      }
    ))

    if (mappings.length < GROUP_MAPPING_DEFAULT_ROWS_SIZE) {
      return [
        ...mappings,
        ...(generateEmptyMappings(GROUP_MAPPING_DEFAULT_ROWS_SIZE - mappings.length))
      ]
    }
    return mappings
  }
)

export const isConfiguredSelector = createSelector(
  scimStateSelector,
  (state) => state === SCIM_STATES.ENABLED
)

export const isDeactivationPopupDisplayedSelector = createSelector(
  sliceSelector,
  ({ isDeactivationPopupDisplayed }) => isDeactivationPopupDisplayed
)

export const isDeleteScimPopupDisplayedSelector = createSelector(
  sliceSelector,
  ({ isDeleteScimPopupDisplayed }) => isDeleteScimPopupDisplayed
)

export const isGroupMappingEnabledSelector = createSelector(
  scimSettingsSelector,
  (settings) => settings?.group_mapping_state === GROUP_MAPPING_STATES.ACTIVATED
)

export const isLoadingSelector = createSelector(
  sliceSelector,
  ({ isLoading }) => isLoading
)
export const isPreviewChangesDisplayedSelector = createSelector(
  sliceSelector,
  ({ isPreviewChangesDisplayed }) => isPreviewChangesDisplayed
)

export const isPreviewInformationLoadingSelector = createSelector(
  sliceSelector,
  ({ isPreviewInformationLoading }) => isPreviewInformationLoading
)

export const isScimConfigurationSavingSelector = createSelector(
  sliceSelector,
  ({ isScimConfigurationSaving }) => isScimConfigurationSaving
)

export const isScimFeatureEnabledSelector = (state) => isAvailableFeatureSelector(state, { featureKey: 'scim_user_management' })

export const isSyncErrorSelector = createSelector(
  scimStateSelector,
  (state) => state === SCIM_STATES.SYNC_ERROR
)
export const isSyncingSelector = createSelector(
  scimStateSelector,
  (state) => state === SCIM_STATES.SYNCING
)

export const isUserGroupsLoadingSelector = createSelector(
  sliceSelector,
  ({ isUserGroupsLoading }) => isUserGroupsLoading
)

export const isUserGroupsSyncingSelector = createSelector(
  scimSettingsSelector,
  (settings) => settings?.group_mapping_state === GROUP_MAPPING_STATES.SYNCING
)

export const maximumBearerTokensSelector = createSelector(
  scimSettingsSelector,
  (settings) => settings && settings.maximum_bearer_tokens
)

export const newUserGroupsSelector = createSelector(
  sliceSelector,
  ({ newUserGroups }) => newUserGroups
)

export const previewResponseSelector = createSelector(
  sliceSelector,
  ({ previewResponse }) => previewResponse
)

export const tokenDurationOptionsSelector = createSelector(
  scimSettingsSelector,
  (settings) => {
    if (!settings || !settings.token_durations_options) return []

    return Object.entries(settings.token_durations_options)
      .map(([key, value]) => ({ value: parseInt(key, 10) || null, name: value }))
  }
)

export const userGroupsSelector = createSelector(
  sliceSelector,
  ({ userGroups }) => userGroups
)
